import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { api, resolveImg, isNew } from "@/lib/adminApi";
import ProductBadges from "@/components/jd/ProductBadges";

const MotionLink = motion(Link);

const CATEGORIES = [
  { key: "whiskey", label: "Whiskey", tagline: "The malts, the bourbons, the quiet legends." },
  { key: "spirits", label: "Other Spirits", tagline: "Cognac, mezcal, rum — the neighbouring nations." },
  { key: "glassware", label: "Glassware", tagline: "Tulips, tumblers, decanters. Vessels of ceremony." },
  { key: "apparel", label: "Apparel", tagline: "Tweed, wool, leather. The uniform of the evening." },
  { key: "barware", label: "Barware", tagline: "Jiggers, ice, coasters — the small tools of a proper pour." },
];

export default function TopShelf() {
  const [picks, setPicks] = useState([]);
  const [open, setOpen] = useState("whiskey");

  useEffect(() => {
    api.get(`/picks`).then((res) => setPicks(res.data || [])).catch(() => setPicks([]));
  }, []);

  const grouped = useMemo(() => {
    const m = {};
    for (const p of picks) {
      (m[p.category] = m[p.category] || []).push(p);
    }
    return m;
  }, [picks]);

  return (
    <section
      id="top-shelf"
      data-testid="top-shelf-section"
      className="py-28 md:py-40 relative"
      style={{ background: "var(--surface)" }}
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10">
        <div className="grid grid-cols-12 gap-6 mb-16">
          <div className="col-span-12 lg:col-span-6">
            <div className="eyebrow mb-4">Top Shelf Picks</div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight text-bone">
              The <em className="italic" style={{ color: "var(--amber)" }}>curated</em> shelf.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="font-body text-lg text-bone-dim">
              A rotating cabinet of bottles, glass, and cloth we would keep in our own home.
              Every recommendation is linked — buy where you please, we take a small dram in return.
            </p>
          </div>
        </div>

        <div>
          {CATEGORIES.map((c, i) => {
            const isOpen = open === c.key;
            const allItems = grouped[c.key] || [];
            const items = allItems.slice(0, 3);
            return (
              <div key={c.key} className="acc-item" data-testid={`accordion-${c.key}`}>
                <button
                  className="acc-trigger"
                  data-open={isOpen}
                  onClick={() => setOpen(isOpen ? "" : c.key)}
                  data-testid={`accordion-trigger-${c.key}`}
                >
                  <div className="flex items-baseline gap-6 md:gap-10 flex-1 min-w-0">
                    <span className="chapter-numeral text-2xl md:text-3xl w-10 md:w-14 flex-none">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-3xl md:text-6xl leading-none tracking-tight">
                        {c.label}
                      </div>
                      <div className="eyebrow mt-3" style={{ color: "var(--bone-dim)" }}>
                        {c.tagline}
                      </div>
                    </div>
                  </div>
                  <span className="plus">+</span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {items.map((item, idx) => {
                          const reviewable = item.category === "whiskey" || item.category === "spirits";
                          const inner = (
                            <>
                            <div className="relative aspect-[4/5] overflow-hidden">
                              <img src={resolveImg(item.image_url)} alt={item.name} loading="lazy" className="w-full h-full object-cover" />
                              <div className="absolute top-4 left-4 eyebrow" style={{ color: "var(--bone)" }}>
                                {c.label} · N°{String(idx + 1).padStart(2, "0")}
                              </div>
                              <div className="absolute top-4 right-4 w-9 h-9 border rounded-full flex items-center justify-center" style={{ borderColor: "var(--brass)", background: "rgba(14,13,11,0.35)" }}>
                                <ArrowUpRight size={14} color="var(--bone)" />
                              </div>
                              {isNew(item.created_at) && (
                                <span className="new-badge absolute bottom-4 left-4" data-testid={`pick-new-${item.id}`}>
                                  <span className="dot" /> New
                                </span>
                              )}
                              {reviewable && item.score != null && (
                                <span className="score-badge absolute bottom-4 right-4" data-testid={`pick-score-${item.id}`}>
                                  <span className="val">{Number(item.score) % 1 === 0 ? Number(item.score).toFixed(0) : Number(item.score)}</span>
                                  <span className="max">/10</span>
                                </span>
                              )}
                              <ProductBadges item={item} size={46} />
                            </div>
                            <div className="p-5 flex items-start justify-between gap-4 border-t" style={{ borderColor: "var(--hairline)" }}>
                              <div className="min-w-0">
                                <div className="font-display text-xl leading-tight text-bone truncate">
                                  {item.name}
                                </div>
                                <div className="font-body text-sm text-bone-dim mt-1">{item.detail}</div>
                                {reviewable && <div className="eyebrow mt-2" style={{ color: "var(--amber)" }}>Read Review →</div>}
                              </div>
                              <div className="font-accent text-brass shrink-0" style={{ letterSpacing: "0.1em" }}>
                                {item.price}
                              </div>
                            </div>
                            </>
                          );
                          const common = {
                            "data-testid": `pick-${item.id}`,
                            initial: { opacity: 0, y: 24 },
                            animate: { opacity: 1, y: 0 },
                            transition: { duration: 0.6, delay: idx * 0.06 },
                            className: "tile-frame group flex flex-col",
                          };
                          return reviewable ? (
                            <MotionLink key={item.id} to={`/review/${item.slug || item.id}`} {...common}>{inner}</MotionLink>
                          ) : (
                            <motion.a key={item.id} href={item.affiliate_url} target="_blank" rel="noopener noreferrer sponsored" {...common}>{inner}</motion.a>
                          );
                        })}
                      </div>
                      <div className="flex items-center justify-between border-t pt-6 pb-16" style={{ borderColor: "var(--hairline)" }}>
                        <span className="eyebrow" style={{ color: "var(--bone-dim)" }}>
                          {allItems.length === 0
                            ? "No items yet"
                            : `Showing ${items.length} most recent of ${allItems.length}`}
                        </span>
                        <Link
                          to={`/shelf/${c.key}`}
                          data-testid={`view-all-${c.key}`}
                          className="btn-outline"
                        >
                          View All {c.label} <ArrowRight size={16} />
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
